import React, { useEffect, useState, useCallback } from "react";

import UploadZone from "./components/UploadZone";
import FileTable from "./components/FileTable";
import BulkEditToolbar from "./components/BulkEditToolbar";
import PreviewModal from "./components/PreviewModal";
import FilmPrintView from "./components/FilmPrintView";
import Login from "./components/Login";
import BracketMark from "./components/BracketMark";

import { listFiles } from "./api/dicomApi";
import {
  getToken,
  getStoredUser,
  clearSession,
} from "./api/authApi";


export default function App() {
  const [user, setUser] = useState(() =>
    getToken() ? getStoredUser() : null
  );

  if (!user) {
    return <Login onAuthenticated={setUser} />;
  }

  return (
    <DicomApp
      user={user}
      onLogout={() => {
        clearSession();
        setUser(null);
      }}
    />
  );
}


/* ================================================================
   MAIN DICOM APPLICATION
================================================================ */

function DicomApp({ user, onLogout }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [printFiles, setPrintFiles] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await listFiles({
        search,
        pageSize: 100,
      });

      setRows(data.rows);
    } catch (err) {
      console.error("Failed to load DICOM files:", err);
    } finally {
      setLoading(false);
    }
  }, [search]);


  useEffect(() => {
    refresh();
  }, [refresh]);


  /* ==============================================================
     SELECTION
  ============================================================== */

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };


  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.length === rows.length
        ? []
        : rows.map((row) => row.id)
    );
  };


  /* ==============================================================
     PRINT
  ============================================================== */

  const openPrintView = () => {
    const selectedRows = rows.filter((row) =>
      selectedIds.includes(row.id)
    );

    setPrintFiles(selectedRows);
  };


  /* ==============================================================
     UI
  ============================================================== */

  return (
    <div className="min-h-screen bg-[#f5f9fb] text-[#344054]">

      {/* ==========================================================
          TOP HEADER
      ========================================================== */}

      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-[#e5edf2]">

        <div className="max-w-[1600px] mx-auto px-5 sm:px-7 lg:px-9">

          <div className="h-[76px] flex items-center justify-between">

            {/* ----------------------------------------------------
                BRAND
            ---------------------------------------------------- */}

            <div className="flex items-center gap-3.5">

              {/* Logo */}
              <div className="relative w-11 h-11 flex-shrink-0">

                <div className="absolute inset-0 rounded-full border-[4px] border-[#15958f]" />

                <div
                  className="absolute inset-0 rounded-full border-[4px] border-transparent border-t-[#2878b4] border-l-[#2878b4]"
                  style={{
                    transform: "rotate(-35deg)",
                  }}
                />

                <div className="absolute inset-0 flex items-center justify-center">

                  <div className="relative w-6 h-6">

                    <div className="absolute left-1/2 top-0 -translate-x-1/2 w-[8px] h-full bg-[#15958f]" />

                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-[8px] bg-[#15958f]" />

                    <svg
                      className="absolute inset-0 w-full h-full"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M1 13H5L7 7L10 18L13 10L15 13H19L21 7L23 13"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>

                  </div>
                </div>
              </div>


              {/* Brand text */}
              <div>

                <div className="flex items-center leading-none">

                  <span className="text-[25px] font-bold tracking-[-1px] text-[#1d6197]">
                    Rx
                  </span>

                  <span className="text-[25px] font-bold tracking-[-1px] text-[#287bb6]">
                    DICOM
                  </span>

                </div>

                <p className="hidden sm:block mt-1 text-[8px] uppercase tracking-[0.16em] text-[#8998a5] font-medium">
                  DIGITAL RADIOLOGY IMAGING SOLUTIONS
                </p>

              </div>
            </div>


            {/* ----------------------------------------------------
                USER AREA
            ---------------------------------------------------- */}

            <div className="flex items-center gap-3 sm:gap-5">

              {/* Status */}
              <div className="hidden md:flex items-center gap-2">

                <span className="relative flex h-2.5 w-2.5">

                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#14b89d] opacity-50" />

                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#14b89d]" />

                </span>

                <span className="text-xs text-[#7a8995]">
                  Secure connection
                </span>

              </div>


              {/* Divider */}
              <div className="hidden sm:block h-8 w-px bg-[#e5edf2]" />


              {/* User */}
              <div className="flex items-center gap-2.5">

                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#dff5f2] to-[#e4f0f8] flex items-center justify-center">

                  <svg
                    className="w-5 h-5 text-[#2675a4]"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  >
                    <circle cx="12" cy="7" r="4" />
                    <path
                      d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7"
                      strokeLinecap="round"
                    />
                  </svg>

                </div>

                <div className="hidden sm:block">

                  <p className="text-sm font-semibold text-[#344054]">
                    {user.username}
                  </p>

                  <p className="text-[10px] text-[#98a5af]">
                    DICOM User
                  </p>

                </div>

              </div>


              {/* Logout */}
              <button
                onClick={onLogout}
                className="
                  group
                  flex
                  items-center
                  gap-2
                  px-3
                  py-2
                  rounded-lg
                  border
                  border-[#e1e9ee]
                  bg-white
                  text-xs
                  font-medium
                  text-[#71808c]
                  hover:border-[#cbdce5]
                  hover:bg-[#f7fafb]
                  hover:text-[#236e9e]
                  transition-all
                "
              >

                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                >
                  <path
                    d="M10 17l5-5-5-5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  <path
                    d="M15 12H3"
                    strokeLinecap="round"
                  />

                  <path
                    d="M21 4v16"
                    strokeLinecap="round"
                  />
                </svg>

                <span className="hidden sm:inline">
                  Log out
                </span>

              </button>

            </div>

          </div>
        </div>
      </header>


      {/* ==========================================================
          MAIN CONTENT
      ========================================================== */}

      <main className="max-w-[1600px] mx-auto px-5 sm:px-7 lg:px-9 py-7">

        {/* ========================================================
            PAGE HEADER
        ======================================================== */}

        <div className="mb-7">

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">

            <div>

              <div className="flex items-center gap-2 mb-2">

                <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-[#e2f4f1]">

                  <svg
                    className="w-4 h-4 text-[#12988d]"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  >
                    <path
                      d="M4 4h16v16H4z"
                      strokeLinejoin="round"
                    />

                    <path
                      d="M8 8h8M8 12h8M8 16h5"
                      strokeLinecap="round"
                    />
                  </svg>

                </span>

                <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#7c8b96]">
                  Imaging Workspace
                </span>

              </div>


              <h1 className="text-2xl sm:text-[30px] font-semibold tracking-tight text-[#263d4d]">
                DICOM Bulk Editor
              </h1>

              <p className="mt-1 text-sm text-[#81909b]">
                Upload, preview, and bulk-edit DICOM metadata
              </p>

            </div>


            {/* File count */}
            <div className="flex items-center gap-3">

              <div className="bg-white border border-[#e5edf2] rounded-xl px-4 py-2.5 shadow-sm">

                <p className="text-[10px] uppercase tracking-wider text-[#98a5ae]">
                  Total Files
                </p>

                <p className="text-lg font-semibold text-[#2877a8] leading-tight">
                  {rows.length}
                </p>

              </div>


              {selectedIds.length > 0 && (
                <div className="bg-[#e4f7f3] border border-[#ccece6] rounded-xl px-4 py-2.5">

                  <p className="text-[10px] uppercase tracking-wider text-[#6b948d]">
                    Selected
                  </p>

                  <p className="text-lg font-semibold text-[#12998d] leading-tight">
                    {selectedIds.length}
                  </p>

                </div>
              )}

            </div>

          </div>

        </div>


        {/* ========================================================
            UPLOAD CARD
        ======================================================== */}

        <section className="bg-white rounded-2xl border border-[#e3ebf0] shadow-[0_4px_18px_rgba(42,72,91,0.05)] p-5 sm:p-6 mb-6">

          <div className="flex items-center gap-3 mb-5">

            <div className="w-10 h-10 rounded-xl bg-[#e5f5f3] flex items-center justify-center">

              <svg
                className="w-5 h-5 text-[#12978d]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              >
                <path
                  d="M12 16V4"
                  strokeLinecap="round"
                />

                <path
                  d="M8 8l4-4 4 4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                <path
                  d="M5 13v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5"
                  strokeLinecap="round"
                />
              </svg>

            </div>

            <div>

              <h2 className="text-sm font-semibold text-[#344b5b]">
                Upload DICOM Files
              </h2>

              <p className="text-xs text-[#8b99a4] mt-0.5">
                Add your medical imaging files to the workspace
              </p>

            </div>

          </div>

          <UploadZone onUploaded={refresh} />

        </section>


        {/* ========================================================
            SEARCH / FILTER BAR
        ======================================================== */}

        <section className="bg-white rounded-2xl border border-[#e3ebf0] shadow-[0_4px_18px_rgba(42,72,91,0.05)] p-4 sm:p-5 mb-5">

          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">

            {/* Search */}
            <div className="relative w-full lg:max-w-[600px]">

              <svg
                className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#91a0aa]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              >
                <circle cx="11" cy="11" r="7" />

                <path
                  d="m20 20-4-4"
                  strokeLinecap="round"
                />
              </svg>


              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search patient name, ID, hospital, or filename..."
                className="
                  w-full
                  h-11
                  pl-12
                  pr-4
                  rounded-xl
                  border
                  border-[#dce6eb]
                  bg-[#fbfcfd]
                  text-sm
                  text-[#344054]
                  placeholder:text-[#9aa7b0]
                  outline-none
                  transition-all
                  focus:bg-white
                  focus:border-[#36a89c]
                  focus:ring-4
                  focus:ring-[#36a89c]/10
                "
              />

              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a0adb5] hover:text-[#52616c]"
                >
                  <svg
                    className="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      d="M6 6l12 12M18 6 6 18"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              )}

            </div>


            {/* Right side */}
            <div className="flex items-center justify-between lg:justify-end gap-4">

              <div className="flex items-center gap-2 text-xs text-[#8a98a2]">

                {loading ? (
                  <>
                    <svg
                      className="w-4 h-4 animate-spin text-[#15988d]"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="9"
                        stroke="currentColor"
                        strokeWidth="3"
                        className="opacity-25"
                      />

                      <path
                        d="M21 12a9 9 0 0 0-9-9"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                      />
                    </svg>

                    <span>
                      Loading files...
                    </span>
                  </>
                ) : (
                  <>
                    <span className="w-2 h-2 rounded-full bg-[#16ae96]" />
                    <span>
                      {rows.length} files available
                    </span>
                  </>
                )}

              </div>


              <button
                type="button"
                onClick={refresh}
                disabled={loading}
                className="
                  flex
                  items-center
                  gap-2
                  h-10
                  px-3.5
                  rounded-lg
                  border
                  border-[#dce6eb]
                  bg-white
                  text-xs
                  font-medium
                  text-[#637580]
                  hover:bg-[#f5f9fb]
                  hover:text-[#21729f]
                  disabled:opacity-50
                  transition-all
                "
              >

                <svg
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                >
                  <path
                    d="M20 11a8 8 0 0 0-14.7-4M4 5v4h4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  <path
                    d="M4 13a8 8 0 0 0 14.7 4M20 19v-4h-4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>

                <span className="hidden sm:inline">
                  Refresh
                </span>

              </button>

            </div>

          </div>

        </section>


        {/* ========================================================
            BULK EDIT TOOLBAR
        ======================================================== */}

        {selectedIds.length > 0 && (
          <div className="mb-5">

            <div className="bg-[#eef9f7] rounded-2xl border border-[#cfece6] p-1">

              <BulkEditToolbar
                selectedIds={selectedIds}
                onApplied={refresh}
                onClear={() => setSelectedIds([])}
                onPrint={openPrintView}
              />

            </div>

          </div>
        )}


        {/* ========================================================
            FILE TABLE
        ======================================================== */}

        <section className="bg-white rounded-2xl border border-[#e3ebf0] shadow-[0_4px_18px_rgba(42,72,91,0.05)] overflow-hidden">

          {/* Table header */}
          <div className="px-5 py-4 border-b border-[#e8eef2] flex items-center justify-between">

            <div className="flex items-center gap-3">

              <div className="w-9 h-9 rounded-lg bg-[#edf5fa] flex items-center justify-center">

                <svg
                  className="w-5 h-5 text-[#2877a8]"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                >
                  <path
                    d="M4 5h16v14H4z"
                    strokeLinejoin="round"
                  />

                  <path
                    d="M8 9h8M8 13h8M8 17h5"
                    strokeLinecap="round"
                  />
                </svg>

              </div>

              <div>

                <h2 className="text-sm font-semibold text-[#344b5b]">
                  DICOM Files
                </h2>

                <p className="text-[11px] text-[#94a1aa]">
                  Patient and imaging metadata
                </p>

              </div>

            </div>


            {selectedIds.length > 0 && (
              <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#e4f7f3] text-[#138f85] text-xs font-medium">

                <span className="w-1.5 h-1.5 rounded-full bg-[#159f91]" />

                {selectedIds.length} selected

              </span>
            )}

          </div>


          {/* Existing table component */}
          <div className="overflow-x-auto">

            <FileTable
              rows={rows}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleSelectAll={toggleSelectAll}
              onChanged={refresh}
              onPreview={setPreviewFile}
            />

          </div>

        </section>


        {/* ========================================================
            FOOTER
        ======================================================== */}

        <footer className="py-7">

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">

            <p className="text-[11px] text-[#a0adb5]">
              Rx DICOM • Digital Radiology Imaging Solutions
            </p>

            <div className="flex items-center gap-3 text-[10px] text-[#b0bbc2]">

              <span>Rx DICOM v2.0</span>

              <span>•</span>

              <span>HIPAA Compliant</span>

              <span>•</span>

              <span>Secure Connection</span>

            </div>

          </div>

        </footer>

      </main>


      {/* ==========================================================
          PREVIEW MODAL
      ========================================================== */}

      <PreviewModal
        file={previewFile}
        allFiles={rows}
        onSelect={setPreviewFile}
        onClose={() => setPreviewFile(null)}
      />


      {/* ==========================================================
          FILM PRINT VIEW
      ========================================================== */}

      {printFiles && (
        <FilmPrintView
          files={printFiles}
          onClose={() => setPrintFiles(null)}
        />
      )}

    </div>
  );
}